import { VE_MOBILE_PREFIXES } from '@common/constants/ve-mobile-prefixes';

/**
 * Lista oficial de bancos de Venezuela (SUDEBAN — Superintendencia de Bancos).
 *
 * Fuente única para los selectores de banco en formularios de pago/cobro del módulo
 * Créditos (request-form al solicitar préstamo, etc.). Centralizar evita drift entre
 * componentes y facilita mantenimiento si cambia la lista oficial.
 *
 * Si en el futuro otro módulo (ventas/pagos) necesita la misma lista, debería
 * consumir esta constante en lugar de duplicarla.
 */
/**
 * ADM-678: banco con su código SUDEBAN de 4 dígitos.
 *
 * La verificación automática de pagos de cuota necesita el CÓDIGO (es el `bankId`
 * que viaja al banco), no el nombre. La fuente en vivo es
 * `GET /api/creditos/banks`; esta lista es el fallback para que el selector del
 * modal de pago nunca quede vacío si el endpoint falla.
 *
 * Se mantiene aparte de `SUDEBAN_BANKS` (abajo) a propósito: esa lista de solo
 * nombres la consume `request-form` para los datos de pago del préstamo, donde no
 * hay código y donde figuran bancos que ya no operan pago móvil (BOD, Banco
 * Industrial, Banco Internacional de Desarrollo). Cambiarle la forma rompería ese
 * formulario sin necesidad.
 */
export interface SudebanBank {
    code: string;
    name: string;
}

export const SUDEBAN_BANKS_WITH_CODE: ReadonlyArray<SudebanBank> = [
    { code: '0102', name: 'Banco de Venezuela' },
    { code: '0104', name: 'Banco Venezolano de Crédito' },
    { code: '0105', name: 'Banco Mercantil' },
    { code: '0108', name: 'Banco Provincial (BBVA)' },
    { code: '0114', name: 'Bancaribe' },
    { code: '0115', name: 'Banco Exterior' },
    { code: '0128', name: 'Banco Caroní' },
    { code: '0134', name: 'Banesco' },
    { code: '0137', name: 'Banco Sofitasa' },
    { code: '0138', name: 'Banco Plaza' },
    { code: '0151', name: 'Banco Fondo Común (BFC)' },
    { code: '0156', name: '100% Banco' },
    { code: '0157', name: 'Delsur Banco' },
    { code: '0163', name: 'Banco del Tesoro' },
    { code: '0166', name: 'Banco Agrícola de Venezuela' },
    { code: '0168', name: 'Bancrecer' },
    { code: '0169', name: 'Mi Banco' },
    { code: '0171', name: 'Banco Activo' },
    { code: '0172', name: 'Bancamiga' },
    { code: '0174', name: 'Banplus' },
    { code: '0175', name: 'Banco Bicentenario' },
    { code: '0177', name: 'Banco de la Fuerza Armada Nacional Bolivariana (BANFANB)' },
    { code: '0191', name: 'Banco Nacional de Crédito (BNC)' },
];

/**
 * Prefijos de operadora válidos para el teléfono de pago móvil: la lista única del front (FINA-3402), la MISMA
 * referencia, no una copia. `0422` es el segundo prefijo de Digitel y el del teléfono receptor de Fina. Créditos toma
 * `PAGO_MOVIL_PREFIXES[1]` (0414) como prefijo por defecto, por eso la lista base conserva el orden ascendente.
 */
export const PAGO_MOVIL_PREFIXES: ReadonlyArray<string> = VE_MOBILE_PREFIXES;

export const SUDEBAN_BANKS: ReadonlyArray<string> = [
    '100% Banco',
    'Banco Activo',
    'Banco Agrícola de Venezuela',
    'Banco Bicentenario',
    'Banco Caroní',
    'Banco de la Fuerza Armada Nacional Bolivariana (BANFANB)',
    'Banco de Venezuela',
    'Banco del Tesoro',
    'Banco Exterior',
    'Banco Fondo Común (BFC)',
    'Banco Industrial de Venezuela',
    'Banco Internacional de Desarrollo',
    'Banco Mercantil',
    'Banco Nacional de Crédito (BNC)',
    'Banco Occidental de Descuento (BOD)',
    'Banco Plaza',
    'Banco Provincial (BBVA)',
    'Banco Sofitasa',
    'Banco Venezolano de Crédito',
    'Banesco',
    'Bancamiga',
    'Bancaribe',
    'Bancrecer',
    'Banplus',
    'Delsur Banco',
    'Mi Banco',
];

/**
 * Opción del selector de banco (`fina-bank-select`): con código SUDEBAN cuando se conoce. La lista
 * de solo nombres (`SUDEBAN_BANKS`) se cruza con `SUDEBAN_BANKS_WITH_CODE` para poder buscar por
 * código también en la solicitud; los bancos sin código (BOD, Industrial, BID) van sin él.
 */
export interface BankOption {
    code: string | null;
    name: string;
}

const CODE_BY_NAME = new Map(SUDEBAN_BANKS_WITH_CODE.map(b => [b.name, b.code]));

export const SUDEBAN_BANK_OPTIONS: ReadonlyArray<BankOption> = SUDEBAN_BANKS.map(name => ({ code: CODE_BY_NAME.get(name) ?? null, name }));
